import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface BotMetricsDetailProps {
  bot: {
    id: string;
    name: string;
    uptime: number;
    memory: number;
    cpu: number;
    requests: number;
  };
}

// Mock metrics data
const mockMetrics = {
  performance: {
    responseTime: [
      { time: '00:00', avg: 120, p95: 250, p99: 450 },
      { time: '00:05', avg: 110, p95: 230, p99: 420 },
      { time: '00:10', avg: 135, p95: 280, p99: 500 },
      { time: '00:15', avg: 105, p95: 220, p99: 380 },
      { time: '00:20', avg: 125, p95: 260, p99: 460 }
    ],
    throughput: [
      { time: '00:00', requests: 45, errors: 2 },
      { time: '00:05', requests: 52, errors: 1 },
      { time: '00:10', requests: 38, errors: 3 },
      { time: '00:15', requests: 61, errors: 0 },
      { time: '00:20', requests: 48, errors: 1 }
    ]
  },
  resources: {
    cpu: [
      { time: '00:00', usage: 35 },
      { time: '00:05', usage: 42 },
      { time: '00:10', usage: 38 },
      { time: '00:15', usage: 45 },
      { time: '00:20', usage: 41 }
    ],
    memory: [
      { time: '00:00', usage: 324 },
      { time: '00:05', usage: 338 },
      { time: '00:10', usage: 351 },
      { time: '00:15', usage: 329 },
      { time: '00:20', usage: 342 }
    ]
  },
  errors: {
    byType: [
      { name: 'Timeout', value: 15, color: '#ef4444' },
      { name: 'Rate Limit', value: 8, color: '#f59e0b' },
      { name: 'Parse Error', value: 5, color: '#8b5cf6' },
      { name: 'Network', value: 3, color: '#06b6d4' }
    ],
    timeline: [
      { time: '00:00', count: 2 },
      { time: '00:05', count: 1 },
      { time: '00:10', count: 3 },
      { time: '00:15', count: 0 },
      { time: '00:20', count: 1 }
    ]
  },
  calls: {
    duration: [
      { range: '0-30s', count: 45 },
      { range: '30-60s', count: 67 },
      { range: '60-120s', count: 89 },
      { range: '120-300s', count: 34 },
      { range: '300s+', count: 12 }
    ],
    outcomes: [
      { name: 'Successful', value: 187, color: '#10b981' },
      { name: 'Abandoned', value: 23, color: '#f59e0b' },
      { name: 'Failed', value: 12, color: '#ef4444' },
      { name: 'Transferred', value: 8, color: '#8b5cf6' }
    ]
  }
};

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  change?: number;
  status?: 'success' | 'warning' | 'error';
}

function MetricCard({ title, value, subtitle, change, status }: MetricCardProps) {
  const getStatusColor = () => {
    switch (status) {
      case 'success': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'error': return 'text-red-600';
      default: return 'text-gray-900';
    }
  };

  const getChangeColor = () => {
    if (change === undefined) return '';
    return change > 0 ? 'text-red-600' : 'text-green-600';
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className={`text-2xl font-bold ${getStatusColor()}`}>
          {value}
        </div>
        <div className="text-sm font-medium text-gray-900">{title}</div>
        {subtitle && (
          <div className="text-xs text-muted-foreground">{subtitle}</div>
        )}
        {change !== undefined && (
          <div className={`text-xs font-medium ${getChangeColor()}`}>
            {change > 0 ? '+' : ''}{change}% vs last hour
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

export function BotMetricsDetail({ bot }: BotMetricsDetailProps) {
  return (
    <div className="space-y-6">
      {/* Key Metrics Summary */}
      <div className="grid grid-cols-4 gap-4">
        <MetricCard
          title="Response Time"
          value="125ms"
          subtitle="Average"
          change={-5}
          status="success"
        />
        <MetricCard
          title="Requests/min"
          value="48"
          subtitle="Current rate"
          change={12}
        />
        <MetricCard
          title="Success Rate"
          value="98.2%"
          subtitle="Last hour"
          status="success"
        />
        <MetricCard
          title="Error Rate"
          value="1.8%"
          subtitle="Last hour"
          change={-15}
          status="warning"
        />
      </div>

      {/* Detailed Metrics */}
      <Tabs defaultValue="performance" className="space-y-4">
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="resources">Resources</TabsTrigger>
          <TabsTrigger value="errors">Errors</TabsTrigger>
          <TabsTrigger value="calls">Calls</TabsTrigger>
        </TabsList>

        <TabsContent value="performance" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Response Time</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={mockMetrics.performance.responseTime}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="time" tick={{ fontSize: 10 }} stroke="#6b7280" />
                    <YAxis tick={{ fontSize: 10 }} stroke="#6b7280" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                    />
                    <Line type="monotone" dataKey="avg" stroke="#3b82f6" strokeWidth={2} name="Average" />
                    <Line type="monotone" dataKey="p95" stroke="#f59e0b" strokeWidth={2} name="P95" />
                    <Line type="monotone" dataKey="p99" stroke="#ef4444" strokeWidth={2} name="P99" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Throughput</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={mockMetrics.performance.throughput}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="time" tick={{ fontSize: 10 }} stroke="#6b7280" />
                    <YAxis tick={{ fontSize: 10 }} stroke="#6b7280" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="requests" fill="#10b981" name="Requests" />
                    <Bar dataKey="errors" fill="#ef4444" name="Errors" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="resources" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">CPU Usage</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={mockMetrics.resources.cpu}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="time" tick={{ fontSize: 10 }} stroke="#6b7280" />
                    <YAxis tick={{ fontSize: 10 }} stroke="#6b7280" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                    />
                    <Line type="monotone" dataKey="usage" stroke="#8b5cf6" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Memory Usage</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={mockMetrics.resources.memory}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="time" tick={{ fontSize: 10 }} stroke="#6b7280" />
                    <YAxis tick={{ fontSize: 10 }} stroke="#6b7280" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                    />
                    <Line type="monotone" dataKey="usage" stroke="#06b6d4" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="errors" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Error Types</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={mockMetrics.errors.byType}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {mockMetrics.errors.byType.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Error Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={mockMetrics.errors.timeline}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="time" tick={{ fontSize: 10 }} stroke="#6b7280" />
                    <YAxis tick={{ fontSize: 10 }} stroke="#6b7280" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="count" fill="#ef4444" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="calls" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Call Duration Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={mockMetrics.calls.duration}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="range" tick={{ fontSize: 10 }} stroke="#6b7280" />
                    <YAxis tick={{ fontSize: 10 }} stroke="#6b7280" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="count" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Call Outcomes</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={mockMetrics.calls.outcomes}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {mockMetrics.calls.outcomes.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}